// R5-3 — THE BROWSER DOWNLOAD PATH, LIFTED OUT OF App.tsx VERBATIM.
//
// Two pure laws (`escapePdfText`, `filenameFromDisposition`) and two DOM writers that use them.
// The pure two are the ones a gate can hold to account, and both have already been the site of a
// real bug class: an unescaped paren corrupts a PDF, and a second filename implementation is how
// one download ends up with two names (T1-DL). They are asserted in `scripts/r5-extraction.mjs`.
//
// **R56 CLOSED THE DEBT THIS HEADER DECLARED.** The note here read "`downloadReportPdf` stays in
// App.tsx for now — it is typed on `AnyReport`, which is declared among the report views", and R11
// moved `AnyReport` out to `src/components/ReportBody.tsx` five rounds ago. The stated blocker was
// gone and nobody went back to look, so the ONE download every delivered reading on this site goes
// through stayed the last member-facing writer in this class that no gate could reach.
//
// **AND MOVING IT WAS ALSO A DERIVATION (R31), BECAUSE THE TWO WRITERS HAD ALREADY DRIFTED.** The
// blob-to-anchor block was typed byte-for-byte twice, and the two `window.alert` sentences had come
// apart — "could not be generated" versus "could not be downloaded" for the same act, one of them
// describing a step that is not where either can fail. `saveBlobAs` is the one anchor law now and
// both writers hand it a response; a gate CALLS it with a fake document and reads back the filename,
// the click and the revoke.

import type { AnyReport } from "../components/ReportBody";

export function escapePdfText(value: string) {
  return value.replace(/\\/g, "\\\\").replace(/\(/g, "\\(").replace(/\)/g, "\\)");
}

export function downloadReadingPdf(title: string, body: string) {
  const safeTitle = title.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase() || "12th-house-reading";
  const wrapped = body
    .split(/\r?\n/)
    .flatMap((line) => {
      if (!line.trim()) return [""];
      const chunks = line.match(/.{1,82}(\s|$)/g);
      return chunks ? chunks.map((chunk) => chunk.trim()) : [line];
    })
    .slice(0, 34);
  const textCommands = wrapped.map((line, index) => `BT /F1 10 Tf 54 ${742 - index * 18} Td (${escapePdfText(line)}) Tj ET`).join("\n");
  const objects = [
    "1 0 obj << /Type /Catalog /Pages 2 0 R >> endobj",
    "2 0 obj << /Type /Pages /Kids [3 0 R] /Count 1 >> endobj",
    "3 0 obj << /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Resources << /Font << /F1 4 0 R >> >> /Contents 5 0 R >> endobj",
    "4 0 obj << /Type /Font /Subtype /Type1 /BaseFont /Times-Roman >> endobj",
    `5 0 obj << /Length ${textCommands.length} >> stream\n${textCommands}\nendstream endobj`,
  ];
  let pdf = "%PDF-1.4\n";
  const offsets = [0];
  objects.forEach((object) => {
    offsets.push(pdf.length);
    pdf += `${object}\n`;
  });
  const xref = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.slice(1).forEach((offset) => {
    pdf += `${String(offset).padStart(10, "0")} 00000 n \n`;
  });
  pdf += `trailer << /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`;

  const url = URL.createObjectURL(new Blob([pdf], { type: "application/pdf" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = `${safeTitle}.pdf`;
  link.click();
  URL.revokeObjectURL(url);
}

// T1-DL: read the filename the SERVER chose. RFC 5987 filename* wins when present (it carries
// non-ASCII correctly); plain filename is the fallback. Returns empty when the header says
// nothing, and the caller supplies a last-resort name rather than inventing a scheme of its own.
export function filenameFromDisposition(header: string | null): string {
  const h = String(header || "");
  const star = h.match(/filename\*=(?:UTF-8'')?"?([^";]+)"?/i);
  if (star?.[1]) { try { return decodeURIComponent(star[1]); } catch { return star[1]; } }
  const plain = h.match(/filename="?([^";]+)"?/i);
  return plain?.[1] || "";
}

// D6-3: her ranged tabs are CSV-exportable, and the file is written from the payload ALREADY ON
// SCREEN (`toCsv` in src/commandCenter.js builds the text; this writes it). It rides the same
// anchor-and-revoke path as every other download here rather than growing a second one — a blob URL
// that is never revoked is a leak in a dashboard she leaves open all day.
export function downloadCsv(filename: string, text: string) {
  // The BOM is not decoration: without it Excel reads a UTF-8 CSV as the local codepage, and her
  // room names come back mangled.
  const blob = new Blob([`\uFEFF${text}`], { type: "text/csv;charset=utf-8" });
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = href;
  anchor.download = filename || "12thhouse.csv";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(href), 1500);
}

// T1-DL: download a server-generated PDF from a URL as a REAL attachment, taking the filename the
// server states. Used by every PDF that is fetched by URL rather than posted (seal sheets today).
// It never opens a tab: a viewer tab is a dead end on a phone and a puzzle on a desktop.
// THE ONE ANCHOR LAW. A response in, a file on her disk out. **The name is read off
// Content-Disposition and never recomputed** (T1-DL: two implementations of one rule is how a
// download ends up with two names), and the object URL is revoked, because a page that opens twenty
// readings leaks twenty blobs otherwise.
export async function saveBlobAs(res: Response, doc: Document = document): Promise<boolean> {
  if (!res.ok) { await res.body?.cancel?.().catch(() => {}); return false; }
  const blob = await res.blob();
  const href = URL.createObjectURL(blob);
  const anchor = doc.createElement("a");
  anchor.href = href;
  anchor.download = filenameFromDisposition(res.headers.get("content-disposition")) || "12thHouse.pdf";
  doc.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(href), 1500);
  return true;
}

// The ONE sentence a failed download says, in both directions. It was two sentences that described
// two different steps, for one act that fails in the same place either way.
export const DOWNLOAD_FAILED_FALLBACK = "The PDF could not be downloaded right now. Please try again.";

// ---------------------------------------------------------------------------------------------
// R101 — **THE LAST TWO BLIND FETCH SITES ON THIS SITE, AND THEY ARE THE ONE DOWNLOAD EVERY
// DELIVERED READING GOES THROUGH.**
//
// Both writers below sent a request, threw the answer away, and said ONE English sentence to every
// refusal there is. Measured, that is what each route actually answers:
//
//   `/api/report/pdf`
//     * **402 + `locked: true`** — T8-2's bytes lock. Live and not hypothetical: it exists because a
//       Newbie downloaded a Vet spell by posting the recipe here as a report. `requireFeature` always
//       sends `locked: true`, so **T8-3's interceptor has ALREADY raised her purple box** — and the
//       old code then fired a BLOCKING NATIVE ALERT over it saying *please try again*, about a door
//       that does not open by trying again. Two answers to one refusal, and the wrong one on top.
//     * **401** "Sign in first." — the one refusal she can fix in a single tap (R61/R98).
//     * **502 x3**, each naming exactly what failed and each saying the retry IS worth it ("Your seal
//       could not be prepared with its Seal of God right now. Please try the download again.").
//     * **400** "No report provided." and **500** "Could not generate the PDF."
//   `/api/seals/generated/:id/pdf/:kind`
//     * **401** "Sign in.", **404** "Seal not found." — and *please try again* about a seal that is
//       not there is a lie the member can act on, and will — and **500**.
//
// Nine sentences the server composed; not one had ever reached a member (R51/R60/R66's shape).
//
// **THE RULING ON SILENCE, AND IT IS THE ONE THAT NEEDED ARGUING.** A `locked` answer draws NOTHING
// here. That is not a swallowed failure: it is T8-3's own law ("one event, one box") applied to the
// fifty-first surface. The box she is looking at names the tier, the price and the button; the alert
// on top of it names none of those and blocks her from reading it.
//
// **`window.alert` STAYS THE CARRIER, and that is deliberate rather than settled.** Nine call sites
// render a delivered reading and none has a status line for this; replacing the carrier is a product
// change and is flagged for her word, not taken inside a correctness round. So is `failedMessage`'s
// language (it is on her open list). What changes is that the SENTENCE is now the server's own
// wherever the server sent one.
//
// `doFetch`, `doc` and `say` are PARAMETERS (R46), so every answer either route can give is
// reachable with no browser, no session and no database.
// ---------------------------------------------------------------------------------------------

export type DownloadAnswer = {
  // `ok` the bytes are on her disk. `locked` the purple box is already answering. `refused` the
  // server said why. `offline` nothing usable arrived, which is the ONLY state the house fallback
  // is for (R18: an answer the server DELIBERATELY SENT is not transport noise).
  kind: "ok" | "locked" | "refused" | "offline";
  // What the SERVER said, or "" when it said nothing. Never the house's own fallback: that is chosen
  // by `downloadSentence`, so a caller cannot mistake our sentence for the route's.
  message: string;
  // What the bytes were, on an `ok` answer only. Read by `inAppSaveSentence` (IT #319).
  contentType?: string;
};

export type DownloadDeps = {
  /** IT #319 — injectable so a gate drives the in-app-browser answer with no phone. */
  cannotSaveFiles?: boolean;
  doFetch?: typeof fetch;
  /** HER ORDER 2026-09-18 — injectable so a gate drives the loss report with nothing on the wire. */
  report?: (kind: "card-image" | "document", info?: { url?: string; detail?: string }) => unknown;
  doc?: Document;
  say?: (message: string) => void;
};

const houseDocument = () => (typeof document === "undefined" ? (null as unknown as Document) : document);
const houseAlert = (message: string) => { if (typeof window !== "undefined") window.alert(message); };

// PURE: what a NON-ok answer means. `locked` is asked of the payload rather than of the status alone,
// because a 402 carrying no lock is a refusal the box cannot render and she would then be told
// nothing at all.
export function downloadRefusal(status: number, body: { locked?: unknown; message?: unknown } | null): DownloadAnswer {
  const said = typeof body?.message === "string" ? body.message.trim() : "";
  if (status === 402 && body?.locked === true) return { kind: "locked", message: said };
  return said ? { kind: "refused", message: said } : { kind: "offline", message: "" };
}

// PURE: what she is TOLD. "" is silence, and it is only ever right for bytes that landed and for a
// door that has already answered.
export function downloadSentence(answer: DownloadAnswer, failedMessage: string = DOWNLOAD_FAILED_FALLBACK): string {
  if (answer.kind === "ok" || answer.kind === "locked") return "";
  return answer.message || failedMessage;
}

// The one transport. A thrown request and a body that will not parse are both `offline` — R18's
// distinction, on the half `saveBlobAs` could never see.
export async function runDownload(send: () => Promise<Response>, doc: Document = houseDocument()): Promise<DownloadAnswer> {
  let res: Response;
  try {
    res = await send();
  } catch {
    return { kind: "offline", message: "" };
  }
  if (res.ok) {
    try {
      return (await saveBlobAs(res, doc)) ? { kind: "ok", message: "", contentType: res.headers.get("content-type") || "" } : { kind: "offline", message: "" };
    } catch {
      return { kind: "offline", message: "" };
    }
  }
  const body = await res.json().then((d) => d as { locked?: unknown; message?: unknown } | null).catch(() => null);
  return downloadRefusal(res.status, body);
}

// ---------------------------------------------------------------------------------------------
// >>> **IT #319 — "THE DOWNLOAD BUTTON ON MY SEAL IS NOT RESPONDING."** <<<
//
// Inside an app's own browser (Instagram, Facebook, TikTok, an Android webview) a file saved from a
// page's script goes NOWHERE: the anchor is clicked, nothing is written and nothing is said, so every
// answer above reads `ok` and the button looks dead. Her Android browser "cannot do web push", which
// real Chrome can, and that is this envelope. **Nothing here can tell whether the bytes landed**, so
// the house says, once, how to keep the file anyway: a picture can be pressed and held, and anything
// downloads in Chrome or Safari. Which browser this is gets decided ONCE, by `cannotSaveFiles` in
// `src/clientBeacon.js`, and handed in at boot through `setDownloadEnvironment`.
// ---------------------------------------------------------------------------------------------
let houseCannotSaveFiles = false;
export function setDownloadEnvironment(env: { cannotSaveFiles?: boolean } = {}) {
  houseCannotSaveFiles = env.cannotSaveFiles === true;
}

// PURE: what she is told after a download inside an app's browser. "" everywhere else.
export function inAppSaveSentence(answer: DownloadAnswer, cannotSaveFiles: boolean): string {
  if (!cannotSaveFiles || answer.kind !== "ok") return "";
  return /^image\//i.test(String(answer.contentType || ""))
    ? "If it did not save, press and hold the picture and choose Save image. The app you opened this link in cannot save files, and in Chrome or Safari this button downloads it."
    : "If it did not save, open 12thhouse.ai in Chrome or Safari and download it there. The app you opened this link in cannot save files.";
}

export async function downloadFromUrl(url: string, failedMessage: string = DOWNLOAD_FAILED_FALLBACK, deps: DownloadDeps = {}): Promise<DownloadAnswer> {
  const answer = await runDownload(() => (deps.doFetch || fetch)(url), deps.doc || houseDocument());
  const sentence = downloadSentence(answer, failedMessage) || inAppSaveSentence(answer, deps.cannotSaveFiles ?? houseCannotSaveFiles);
  if (sentence) (deps.say || houseAlert)(sentence);
  // >>> **HER ORDER, 2026-09-18 — THE DOCUMENT PATH REPORTS ITS OWN LOSS (#278/#279).** <<<
  //
  // Two members: *"the put_in_a_document tool appears to be failing silently."* **The server is not
  // failing** — measured over fourteen days, every single ask wrote its row, 44 of 44 and 129 of 129.
  // So the loss is between the payload leaving the house and the file reaching her, and THIS is the
  // one transport every document on the site comes down through.
  //
  // **A `locked` ANSWER IS NOT A LOSS** and is deliberately not reported: the purple box is already
  // telling her why, and counting a working wall as a failure is how a number stops meaning anything.
  if (answer.kind === "offline" || answer.kind === "refused") {
    // **INJECTED, NEVER IMPORTED.** This file is one of the pure laws a `.mjs` gate loads and RUNS,
    // and its entry requirement is that it imports nothing but types. The call site hands the
    // reporter in; with nobody watching, a failed download is simply a failed download.
    if (deps.report) deps.report("document", { url, detail: answer.kind + (answer.message ? `: ${answer.message}` : "") });
  }
  return answer;
}

// **THE DOWNLOAD EVERY DELIVERED READING ON THIS SITE GOES THROUGH** — nine call sites in App.tsx.
// The SERVER names the file; this side only obeys it.
export async function downloadReportPdf(report: AnyReport, failedMessage: string = DOWNLOAD_FAILED_FALLBACK, deps: DownloadDeps = {}): Promise<DownloadAnswer> {
  const answer = await runDownload(() => (deps.doFetch || fetch)("/api/report/pdf", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ report }),
  }), deps.doc || houseDocument());
  const sentence = downloadSentence(answer, failedMessage) || inAppSaveSentence(answer, deps.cannotSaveFiles ?? houseCannotSaveFiles);
  if (sentence) (deps.say || houseAlert)(sentence);
  return answer;
}
